import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CardSelectionPage } from '../components/features/legacy';
import { createReading, spendWalletForReading } from '../lib/api';
import { APP_FEATURES } from '../config/runtime';
import { getAuthSession, startGoogleLogin } from '../lib/auth';
import { trackEvent } from '../lib/analytics';
import { useReadingFlowBackGuard } from '../lib/navigationGuard';
import type { SpreadType, Topic } from '../lib/types';
import UiIcon from '../components/common/UiIcon';
import { getSpreadChuruCost, saveWalletRechargeIntent } from '../lib/walletFlow';

const PENDING_READING_KEY = 'ny_pending_payment_reading';
const DEFAULT_SPREAD: SpreadType = 'three-card';

type SelectedTopic = Topic | null;

interface StitchArchivePageProps {
  eyebrow: string;
  title: string;
  description: string;
  primaryLabel: string;
  onPrimaryAction: () => void;
  secondaryLabel: string;
  onSecondaryAction: () => void;
}

const StitchArchivePage = ({
  eyebrow,
  title,
  description,
  primaryLabel,
  onPrimaryAction,
  secondaryLabel,
  onSecondaryAction,
}: StitchArchivePageProps) => (
  <div className="min-h-screen bg-[#131110] px-6 py-12 text-[#f3e0c7] md:px-10">
    <div className="mx-auto w-full max-w-4xl border border-[#fbbf24]/20 bg-[#17120f]/85 p-6 shadow-[0_30px_80px_-35px_rgba(0,0,0,0.85)] backdrop-blur-sm md:p-10">
      <p className="text-[11px] uppercase tracking-[0.28em] text-[#fbbf24]/65">{eyebrow}</p>
      <h1 className="mt-4 text-3xl font-semibold tracking-[0.04em] text-[#fbbf24] md:text-5xl">{title}</h1>
      <p className="mt-5 max-w-2xl text-sm leading-7 text-[#e7d5bd]/85 md:text-base">{description}</p>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <article className="border border-[#fbbf24]/15 bg-black/20 p-5">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#fbbf24]/55">현재 흐름</p>
          <p className="mt-3 text-sm leading-7 text-[#ead8bf]/85">
            현재 서비스의 실제 리딩 흐름은 스프레드 선택부터 결제, 카드 공개, 결과 페이지까지 새 UI 기준으로 진행됩니다.
          </p>
        </article>
        <article className="border border-[#fbbf24]/15 bg-black/20 p-5">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#fbbf24]/55">이 페이지의 역할</p>
          <p className="mt-3 text-sm leading-7 text-[#ead8bf]/85">
            이 경로는 예전 Stitch 시안과 모션 실험을 보관하기 위한 아카이브입니다. 현재 운영 화면과 혼동되지 않도록 안내만 남겨두었습니다.
          </p>
        </article>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={onPrimaryAction}
          className="flex-1 border border-[#fbbf24]/45 bg-[#241811] px-5 py-3 text-sm font-semibold text-[#fbbf24] transition-colors hover:bg-[#2d1d15]"
        >
          {primaryLabel}
        </button>
        <button
          type="button"
          onClick={onSecondaryAction}
          className="flex-1 border border-[#8a6a4b]/45 bg-[#17120f] px-5 py-3 text-sm font-semibold text-[#e7d5bd] transition-colors hover:bg-[#201712]"
        >
          {secondaryLabel}
        </button>
      </div>
    </div>
  </div>
);

const getCardCount = (spread: SpreadType | null): number => {
  if (!spread) return 3;

  switch (spread) {
    case 'one-card':
      return 1;
    case 'three-card':
      return 3;
    case 'celtic-cross':
      return 10;
    default:
      return 3;
  }
};

export const StitchSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const spreadParam = searchParams.get('spread') as SpreadType | null;
  const spreadFromStorage = sessionStorage.getItem('selected_spread') as SpreadType | null;
  const spread = spreadParam || spreadFromStorage;
  const resolvedSpread = spread ?? DEFAULT_SPREAD;
  const selectedQuestion = sessionStorage.getItem('selected_question') || undefined;

  useReadingFlowBackGuard(true);

  const maxCards = getCardCount(resolvedSpread);
  const topic = sessionStorage.getItem('selected_theme') as SelectedTopic;
  const cardSelectionPersistKey = `stitch-selection:${resolvedSpread}:${topic ?? 'unknown'}:${selectedQuestion ?? 'none'}`;

  const [isRevealLoading, setIsRevealLoading] = useState(false);
  const [revealProgress, setRevealProgress] = useState(0);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const progressTimerRef = useRef<number | null>(null);

  const clearProgressTimer = () => {
    if (progressTimerRef.current !== null) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const finalizeWithReading = (nextReadingId: string) => {
    sessionStorage.setItem('selected_spread', resolvedSpread);
    navigate(`/spread-layout/${nextReadingId}`);
  };

  useEffect(() => {
    if (!spread) {
      sessionStorage.setItem('selected_spread', resolvedSpread);
    }

    return () => {
      clearProgressTimer();
    };
  }, [resolvedSpread, spread]);

  const handleRevealFate = async () => {
    if (isRevealLoading) return;

    let loadingStarted = false;

    try {
      if (!topic) {
        throw new Error('주제를 먼저 선택한 뒤 진행해주세요.');
      }

      if (APP_FEATURES.googleLoginEnabled) {
        const session = await getAuthSession();
        if (!session) {
          setShowLoginPrompt(true);
          return;
        }
      }

      trackEvent('cards_selected', {
        spread: resolvedSpread,
        topic: topic ?? '',
        card_count: maxCards,
      });

      setIsRevealLoading(true);
      setRevealProgress(0);
      clearProgressTimer();
      loadingStarted = true;

      progressTimerRef.current = window.setInterval(() => {
        setRevealProgress((prev) => {
          if (prev >= 92) return prev;
          const next = prev + Math.floor(Math.random() * 9) + 3;
          return Math.min(next, 92);
        });
      }, 120);

      const reading = await createReading(topic, selectedQuestion, resolvedSpread);
      window.sessionStorage.setItem('selected_spread', resolvedSpread);
      window.sessionStorage.setItem(`reading_${reading.readingId}`, reading.replayToken);
      window.sessionStorage.setItem(
        PENDING_READING_KEY,
        JSON.stringify({
          readingId: reading.readingId,
          replayToken: reading.replayToken,
          spread: resolvedSpread,
        }),
      );
      window.sessionStorage.removeItem(cardSelectionPersistKey);
      await spendWalletForReading(reading.readingId, resolvedSpread);
      finalizeWithReading(reading.readingId);
    } catch (error) {
      if (loadingStarted) {
        clearProgressTimer();
        setRevealProgress(100);
      }

      const apiError = error as Error & { code?: string };
      const errorMessage = apiError.message || '리딩 생성에 실패했습니다.';
      if (errorMessage.includes('로그인') || errorMessage.includes('UNAUTHORIZED')) {
        setShowLoginPrompt(true);
        return;
      }

      if (apiError.code === 'INSUFFICIENT_POINTS') {
        const pendingRaw = window.sessionStorage.getItem(PENDING_READING_KEY);
        let readingId = '';
        let replayToken = '';
        if (pendingRaw) {
          try {
            const pending = JSON.parse(pendingRaw) as { readingId: string; replayToken: string; spread?: SpreadType };
            readingId = pending.readingId;
            replayToken = pending.replayToken;
          } catch {
            readingId = '';
            replayToken = '';
          }
        }

        if (readingId && replayToken) {
          saveWalletRechargeIntent({
            type: 'reading',
            readingId,
            replayToken,
            spread: resolvedSpread,
            requiredPoints: getSpreadChuruCost(resolvedSpread),
          });
        }

        navigate('/wallet/recharge');
        return;
      }

      alert(errorMessage);
      return;
    } finally {
      if (loadingStarted) {
        clearProgressTimer();
        setRevealProgress(100);
        window.setTimeout(() => {
          setIsRevealLoading(false);
        }, 260);
      }
    }
  };

  const handleLoginFromPrompt = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    trackEvent('login_started', { source: 'card_selection', return_to: returnTo });
    startGoogleLogin(returnTo);
  };

  return (
    <>
      <CardSelectionPage
        maxCards={maxCards}
        persistKey={cardSelectionPersistKey}
        onRevealFate={handleRevealFate}
        isRevealLoading={isRevealLoading}
        revealProgress={revealProgress}
      />

      {showLoginPrompt && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/75 px-6">
          <div className="w-full max-w-md border border-[#fbbf24]/30 bg-[#1a1512] p-6 shadow-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#fbbf24]/85">
              <UiIcon name="warning" className="h-4 w-4" />
              <span>로그인이 필요합니다</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#dcc7a9]">
              리딩을 시작하려면 Google 로그인이 필요합니다.
              <br />
              고른 카드와 질문은 그대로 유지됩니다.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={handleLoginFromPrompt}
                className="flex-1 border border-[#8a6a4b] bg-[#2a1d17] px-4 py-3 text-sm font-semibold text-[#f5e0c9] hover:bg-[#35251d]"
              >
                Google 로그인
              </button>
              <button
                type="button"
                onClick={() => setShowLoginPrompt(false)}
                className="border border-[#6d5848] bg-[#1a1512] px-4 py-3 text-sm text-[#d6c0a2] hover:border-[#8a6a4b]"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const StitchResult = () => {
  const navigate = useNavigate();

  return (
    <StitchArchivePage
      eyebrow="Stitch Archive"
      title="구버전 결과 시안 보기"
      description="이 화면은 예전 결과 연출 시안을 정리해 둔 아카이브입니다. 현재 서비스의 실제 결과는 카드별 해석과 마지막 종합 리딩이 분리된 결과 페이지로 이어집니다."
      primaryLabel="현재 결과 화면 보기"
      onPrimaryAction={() => navigate('/result/demo-three-card?llm=mock')}
      secondaryLabel="스프레드 선택으로 가기"
      onSecondaryAction={() => navigate('/spreads')}
    />
  );
};

export const StitchSummary = () => {
  const navigate = useNavigate();

  return (
    <StitchArchivePage
      eyebrow="Stitch Archive"
      title="구버전 요약 시안 보기"
      description="테스트용 테마 전환과 샘플 문구가 있던 Summary 시안을 보관한 화면입니다. 현재는 결과 페이지에서 전체 요약과 다음 행동을 바로 확인하는 구조를 사용합니다."
      primaryLabel="새 리딩 시작하기"
      onPrimaryAction={() => navigate('/spreads')}
      secondaryLabel="데모 결과 다시 보기"
      onSecondaryAction={() => navigate('/result/demo-three-card?llm=mock')}
    />
  );
};
